import { getCookie } from '@jusbrasil-web/shared-utils-cookies'
import { useEffect, useRef, useState } from 'react'

import { type CompleteSignupNudgeApiResponse } from './api-handler'
import {
  COMPLETE_SIGNUP_NUDGE_API_ROUTE,
  COMPLETE_SIGNUP_NUDGE_FORCE_COOKIE,
  COMPLETE_SIGNUP_NUDGE_FORCE_MAP,
  CompleteSignupNudgeVariant,
} from './constants'
import { isCompleteSignupNudgeVariant } from './variant'

interface UseExperimentResult {
  variant: CompleteSignupNudgeVariant | null
  loading: boolean
}

export function useCompleteSignupNudgeExperiment(
  enabled: boolean,
  basePath: string
): UseExperimentResult {
  const [variant, setVariant] = useState<CompleteSignupNudgeVariant | null>(null)
  const [loading, setLoading] = useState(enabled)
  const resolvedRef = useRef(false)

  useEffect(() => {
    if (!enabled || resolvedRef.current) return
    resolvedRef.current = true
    setLoading(true) // enabled may flip false→true after mount; reflect resolving

    const forced = resolveForcedVariant()
    if (forced) {
      setVariant(forced)
      setLoading(false)
      return
    }

    let active = true
    fetch(`${basePath}${COMPLETE_SIGNUP_NUDGE_API_ROUTE}`, {
      headers: { accept: 'application/json' },
    })
      .then((res) => (res.ok ? (res.json() as Promise<CompleteSignupNudgeApiResponse>) : null))
      .then((data) => {
        if (!active) return
        setVariant(isCompleteSignupNudgeVariant(data?.variant) ? data.variant : null)
      })
      .catch(() => {
        if (active) setVariant(null)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [enabled, basePath])

  return { variant, loading }
}

function resolveForcedVariant(): CompleteSignupNudgeVariant | null {
  const raw = getCookie<string>(null, COMPLETE_SIGNUP_NUDGE_FORCE_COOKIE)
  return raw ? COMPLETE_SIGNUP_NUDGE_FORCE_MAP[raw] ?? null : null
}
